import React from 'react';
import { Box, Button, MenuItem, Tab, Tabs, TextField, Typography } from '@mui/material';
import { type OverviewAreaCameraPanelModel } from '../../pages/OverviewAreaCameraPanel';
import { SpatialOperationsLiveCamera } from './SpatialOperationsLiveCamera';
import type { SpatialOperationsLiveCameraView } from '../../hooks/useSpatialOperationsLiveCamera';
import { VisualLifecycleExperience } from '../conditionLifecycle/VisualLifecycleExperience';
import type { VisualLifecycleViewModel } from '../../domain/continuousSafety/visualLifecycleModel';
import {
  NO_AUTHORITATIVE_SPATIAL_GEOMETRY,
  SPATIAL_OPERATIONS_ENRICHMENT_LOADING,
  SPATIAL_OPERATIONS_GEOMETRY_LOADING,
  SPATIAL_OPERATIONS_HISTORY_LOADING,
  SPATIAL_OPERATIONS_SCAN_LOADING,
  SPATIAL_OPERATIONS_SUBLINE,
  SPATIAL_OPERATIONS_TITLE,
  type SpatialOperationsCameraChoice,
  type SpatialOperationsConditionChoice,
  type SpatialOperationsScanView,
  type SpatialOperationsStageId,
  type SpatialOperationsStageView,
} from '../../domain/spatialOperations/spatialOperationsPresentation';
import type { SpatialOperationsPolicyView } from '../../domain/spatialOperations/spatialOperationsPolicy';
import {
  historicalScanOpenPath,
  type SpatialOperationsScanHistoryView,
} from '../../domain/spatialOperations/spatialOperationsScanHistory';
import type { SpatialOperationsMode } from '../../domain/spatialOperations/spatialOperationsUrl';

const colors = {
  ink: '#0f172a',
  ink2: '#4a5768',
  ink3: '#7d8798',
  line: '#e3e8ef',
  bg: '#f4f6f9',
  paper: '#ffffff',
  blue: '#1d4ed8',
  blueSoft: '#eef3ff',
};

export type SpatialOperationsShellProps = {
  binding: 'live' | 'fixture';
  siteId: string;
  siteLabel?: string | null;
  loading?: boolean;
  error?: string | null;
  cameras: SpatialOperationsCameraChoice[];
  conditions: SpatialOperationsConditionChoice[];
  selectedCamera: SpatialOperationsCameraChoice | null;
  selectedCondition: SpatialOperationsConditionChoice | null;
  mode: SpatialOperationsMode;
  cameraPanel: OverviewAreaCameraPanelModel | null;
  liveCamera?: SpatialOperationsLiveCameraView | null;
  cameraOnline: boolean | null;
  cameraFreshness?: string | null;
  visualModel: VisualLifecycleViewModel | null;
  stages: SpatialOperationsStageView[];
  selectedStage?: SpatialOperationsStageId;
  scan: SpatialOperationsScanView;
  scanHistory?: SpatialOperationsScanHistoryView;
  policy: SpatialOperationsPolicyView | null;
  recordedEvidenceUrl?: string | null;
  recordedEvidenceHref?: string | null;
  timeZone?: string | null;
  enrichmentPending?: boolean;
  geometryPending?: boolean;
  onNavigate: (path: string) => void;
  onSelectCamera: (cameraId: string) => void;
  onSelectCondition: (conditionId: string) => void;
  onSelectMode: (mode: SpatialOperationsMode) => void;
  onSelectStage?: (stage: SpatialOperationsStageId) => void;
};

function StageCard({
  stage,
  selected,
  onSelect,
}: {
  stage: SpatialOperationsStageView;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      data-testid={`spatial-operations-stage-${stage.id}`}
      aria-pressed={selected}
      onClick={onSelect}
      sx={{
        flex: { xs: '1 1 auto', sm: '0 0 200px' },
        width: { xs: '100%', sm: 'auto' },
        minWidth: { xs: 0, sm: 200 },
        maxWidth: { xs: '100%', sm: 260 },
        textAlign: 'left',
        border: selected ? `2px solid ${colors.blue}` : `1px solid ${colors.line}`,
        bgcolor: selected ? colors.blueSoft : colors.paper,
        borderRadius: 1,
        px: 1.25,
        py: { xs: 1, sm: 1 },
        cursor: 'pointer',
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'column' },
        alignItems: { xs: 'center', sm: 'flex-start' },
        gap: { xs: 1, sm: 0 },
        '&:focus-visible': { outline: `2px solid ${colors.blue}`, outlineOffset: 2 },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          letterSpacing: 0.08,
          color: selected ? colors.blue : colors.ink2,
          minWidth: { xs: 64, sm: 0 },
        }}
      >
        {stage.label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: colors.ink }} data-testid={`spatial-operations-stage-${stage.id}-state`}>
          {stage.state}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: { xs: selected ? 'block' : 'none', sm: 'block' } }}
        >
          {stage.detail}
        </Typography>
      </Box>
    </Box>
  );
}

export function SpatialOperationsShell({
  binding,
  siteId,
  siteLabel,
  loading,
  error,
  cameras,
  conditions,
  selectedCamera,
  selectedCondition,
  mode,
  cameraPanel,
  liveCamera,
  cameraOnline,
  cameraFreshness,
  visualModel,
  stages,
  selectedStage = 'find',
  scan,
  scanHistory = {
    status: 'ready',
    scans: [],
    latest: null,
    historyHref: '/acceptance/reports?tab=scans',
  },
  policy,
  recordedEvidenceUrl,
  recordedEvidenceHref,
  timeZone,
  enrichmentPending,
  geometryPending,
  onNavigate,
  onSelectCamera,
  onSelectCondition,
  onSelectMode,
  onSelectStage,
}: SpatialOperationsShellProps) {
  const verify = stages.find((item) => item.id === 'verify') || null;
  const alerts = stages.find((item) => item.id === 'alerts') || null;
  const cameraOptions = cameras.filter((item) => item.cameraId);
  const conditionOptions = conditions.filter((item) => !selectedCamera?.cameraId || !item.cameraId || item.cameraId === selectedCamera.cameraId);
  const resolvedLive: SpatialOperationsLiveCameraView = liveCamera || (
    cameraOnline === false
      ? { status: 'offline', mjpegUrl: null }
      : { status: cameraPanel?.snapshotUrl ? 'connecting' : 'idle', mjpegUrl: null }
  );
  const shownStages = enrichmentPending
    ? stages.map((stage) => ({
      ...stage,
      state: 'Loading',
      detail: SPATIAL_OPERATIONS_ENRICHMENT_LOADING,
    }))
    : stages;
  const latestHistoricalScan = scanHistory.status === 'ready' ? scanHistory.latest : null;
  const historyWindow = latestHistoricalScan
    ? [latestHistoricalScan.windowStart, latestHistoricalScan.windowEnd].filter(Boolean).join(' – ')
    : null;

  return (
    <Box
      sx={{ px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto', overflowX: 'hidden' }}
      data-testid="spatial-operations-page"
      data-binding={binding}
      data-site-id={siteId || undefined}
    >
      <Typography variant="h4" sx={{ fontWeight: 750, color: colors.ink }} data-testid="spatial-operations-title">
        {SPATIAL_OPERATIONS_TITLE}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }} data-testid="spatial-operations-subline">
        {SPATIAL_OPERATIONS_SUBLINE}
      </Typography>
      {siteLabel || siteId ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }} data-testid="spatial-operations-site">
          Site · {siteLabel || siteId}
        </Typography>
      ) : null}
      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      ) : null}
      {loading ? (
        <Typography color="text.secondary" data-testid="spatial-operations-loading">Loading Spatial Operations…</Typography>
      ) : (
      <Box data-testid="spatial-operations-resolved">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          select
          fullWidth
          size="small"
          label="Area / Camera"
          value={selectedCamera?.cameraId || ''}
          onChange={(event) => onSelectCamera(event.target.value)}
          inputProps={{ 'data-testid': 'spatial-operations-camera-select', 'aria-label': 'Area and camera' }}
          SelectProps={{
            MenuProps: { MenuListProps: { 'data-testid': 'spatial-operations-camera-menu' } as any },
          }}
        >
          {cameraOptions.length ? cameraOptions.map((camera) => (
            <MenuItem key={camera.cameraId} value={camera.cameraId} data-testid={`spatial-operations-camera-option-${camera.cameraId}`}>
              {camera.areaLabel} · {camera.cameraLabel}
            </MenuItem>
          )) : (
            <MenuItem value="" disabled>No cameras available</MenuItem>
          )}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label="Condition"
          value={selectedCondition?.conditionId || ''}
          onChange={(event) => onSelectCondition(event.target.value)}
          inputProps={{ 'data-testid': 'spatial-operations-condition-select', 'aria-label': 'Condition' }}
          SelectProps={{
            MenuProps: { MenuListProps: { 'data-testid': 'spatial-operations-condition-menu' } as any },
          }}
        >
          {conditionOptions.length ? conditionOptions.map((condition) => (
            <MenuItem
              key={condition.conditionId}
              value={condition.conditionId}
              data-testid={`spatial-operations-condition-option-${condition.family || condition.conditionId}`}
              data-condition-family={condition.family}
            >
              {condition.label}
            </MenuItem>
          )) : (
            <MenuItem value="" disabled>No conditions available</MenuItem>
          )}
        </TextField>
      </Box>

      <Tabs
        value={mode}
        onChange={(_event, next) => onSelectMode(next)}
        aria-label="Camera, Spatial, or History"
        data-testid="spatial-operations-mode-tabs"
        sx={{ minHeight: 40, mb: 1.5, borderBottom: `1px solid ${colors.line}` }}
      >
        <Tab value="camera" label="Camera" data-testid="spatial-operations-mode-camera" sx={{ minHeight: 40, fontWeight: 750 }} />
        <Tab value="spatial" label="Spatial" data-testid="spatial-operations-mode-spatial" sx={{ minHeight: 40, fontWeight: 750 }} />
        <Tab value="history" label="History" data-testid="spatial-operations-mode-history" sx={{ minHeight: 40, fontWeight: 750 }} />
      </Tabs>

      <Box
        data-testid="spatial-operations-primary-view"
        sx={{
          border: `1px solid ${colors.line}`,
          borderRadius: 1.5,
          bgcolor: colors.paper,
          p: { xs: 1.25, md: 2 },
          minHeight: { xs: 260, md: 420 },
          mb: 2,
        }}
      >
        {mode === 'camera' ? (
          <Box data-testid="spatial-operations-camera-mode">
            <Typography variant="overline" color="text.secondary">
              {resolvedLive.status === 'live' ? 'Live camera' : 'Current camera view'}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 750 }} data-testid="spatial-operations-camera-label">
              {selectedCamera?.cameraLabel || 'Camera'} · {selectedCamera?.areaLabel || 'Area'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {selectedCondition?.label || 'Condition'}
            </Typography>
            {cameraFreshness && resolvedLive.status !== 'offline' ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {cameraFreshness}
              </Typography>
            ) : null}
            <SpatialOperationsLiveCamera
              status={resolvedLive.status}
              mjpegUrl={resolvedLive.mjpegUrl}
              snapshotUrl={cameraPanel?.snapshotUrl}
              overlays={cameraPanel?.overlays || (cameraPanel?.geometry ? [cameraPanel.geometry] : [])}
              focusZoneId={selectedCondition?.zoneId || cameraPanel?.zoneId}
              cameraLabel={selectedCamera?.cameraLabel}
            />
            {cameraPanel?.livePath ? (
              <Button
                size="small"
                sx={{ mt: 1 }}
                onClick={() => onNavigate(cameraPanel.livePath!)}
                data-testid="spatial-operations-live-view"
              >
                Open Live View
              </Button>
            ) : null}
          </Box>
        ) : null}

        {mode === 'spatial' ? (
          <Box data-testid="spatial-operations-spatial-mode">
            {geometryPending ? (
              <Typography color="text.secondary" data-testid="spatial-operations-geometry-loading">
                {SPATIAL_OPERATIONS_GEOMETRY_LOADING}
              </Typography>
            ) : visualModel?.spatialMode === 'unavailable' || !visualModel ? (
              <Box>
                <Typography data-testid="spatial-operations-no-geometry">
                  {NO_AUTHORITATIVE_SPATIAL_GEOMETRY}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Camera and History remain available from the existing authorities. No floor plan is shown.
                </Typography>
              </Box>
            ) : (
              <VisualLifecycleExperience
                model={visualModel}
                timeZone={timeZone}
                onNavigate={onNavigate}
                density="page"
                surface="spatial"
              />
            )}
          </Box>
        ) : null}

        {mode === 'history' ? (
          <Box data-testid="spatial-operations-history-mode">
            {enrichmentPending ? (
              <Typography color="text.secondary" data-testid="spatial-operations-history-loading">
                {SPATIAL_OPERATIONS_HISTORY_LOADING}
              </Typography>
            ) : visualModel ? (
              <VisualLifecycleExperience
                model={visualModel}
                timeZone={timeZone}
                onNavigate={onNavigate}
                density="page"
                surface="history"
                focus={selectedStage === 'control' ? 'control' : selectedStage === 'alerts' ? 'pattern' : selectedStage === 'verify' ? 'scan' : undefined}
              />
            ) : (
              <Typography color="text.secondary">No linked lifecycle History is available yet.</Typography>
            )}
          </Box>
        ) : null}
      </Box>

      <Box
        data-testid="spatial-operations-lifecycle-strip"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: { xs: 'nowrap', sm: 'nowrap' },
          gap: 1,
          overflowX: { xs: 'hidden', sm: 'auto' },
          width: '100%',
          maxWidth: '100%',
          pb: 0.5,
          mb: 2,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: { xs: 'block', sm: 'none' },
            fontWeight: 800,
            letterSpacing: 0.06,
            color: colors.ink2,
            mb: 0.25,
          }}
          data-testid="spatial-operations-lifecycle-sequence"
        >
          FIND → ALERTS → CONTROL → SINCE → VERIFY
        </Typography>
        {shownStages.map((stage) => (
          <StageCard
            key={stage.id}
            stage={stage}
            selected={selectedStage === stage.id}
            onSelect={() => {
              onSelectStage?.(stage.id);
              if (stage.id === 'find') onSelectMode('camera');
              if (stage.id === 'alerts' || stage.id === 'control' || stage.id === 'since') onSelectMode('history');
            }}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1.5,
        }}
      >
        <Box sx={{ border: `1px solid ${colors.line}`, borderRadius: 1, p: 1.5 }} data-testid="spatial-operations-scan-summary">
          <Typography variant="overline" color="text.secondary">VERIFY · Safety Scan</Typography>
          {enrichmentPending ? (
            <Typography data-testid="spatial-operations-scan-loading">{SPATIAL_OPERATIONS_SCAN_LOADING}</Typography>
          ) : scan.linked ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 750 }} data-testid="spatial-operations-scan-lifecycle">
                {scan.lifecycle === 'Signed'
                  ? 'Signed'
                  : scan.lifecycle === 'Verified'
                    ? 'Verified · Not signed'
                    : scan.lifecycle === 'Draft'
                      ? 'Draft'
                      : (verify?.state || 'Linked')}
              </Typography>
              {scan.confirmedLabel ? <Typography variant="body2">{scan.confirmedLabel}</Typography> : null}
              {scan.usableHoursLabel ? <Typography variant="body2">{scan.usableHoursLabel}</Typography> : null}
              <Typography variant="caption" color="text.secondary" display="block" data-testid="spatial-operations-scan-coverage">
                Coverage: {scan.coverageState || 'unavailable'}
                {scan.finality ? ` · ${scan.finality}` : ''}
                {scan.resultDirectionLabel ? ` · ${scan.resultDirectionLabel}` : ''}
              </Typography>
              {scan.canOpen && scan.openHref ? (
                <Button
                  variant="contained"
                  sx={{ mt: 1.25 }}
                  onClick={() => onNavigate(scan.openHref!)}
                  data-testid="spatial-operations-open-scan"
                >
                  Open Safety Scan
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Safety Scan is linked. Opening the interactive Scan requires existing Scan access.
                </Typography>
              )}
            </>
          ) : (
            <Typography data-testid="spatial-operations-no-scan">{scan.emptyCopy}</Typography>
          )}
          <Box sx={{ borderTop: `1px solid ${colors.line}`, mt: 1.5, pt: 1.5 }} data-testid="spatial-operations-scan-history">
            <Typography variant="overline" color="text.secondary">SAFETY SCAN HISTORY</Typography>
            {scanHistory.status === 'loading' ? (
              <Typography color="text.secondary" data-testid="spatial-operations-scan-history-loading">
                Loading Safety Scan history…
              </Typography>
            ) : scanHistory.status === 'error' ? (
              <Typography color="error" data-testid="spatial-operations-scan-history-error">
                Safety Scan history could not be loaded.
              </Typography>
            ) : scanHistory.scans.length > 0 ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 750 }} data-testid="spatial-operations-scan-history-count">
                  {scanHistory.scans.length} historical Safety Scan{scanHistory.scans.length === 1 ? '' : 's'}
                </Typography>
                <Typography variant="body2" data-testid="spatial-operations-scan-history-window">
                  Latest attributable window: {historyWindow || 'Unavailable'}
                </Typography>
                <Typography variant="body2" data-testid="spatial-operations-scan-history-status">
                  Persisted report status: {latestHistoricalScan?.status || 'Unavailable'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {latestHistoricalScan ? (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => onNavigate(historicalScanOpenPath(latestHistoricalScan.reportId))}
                      data-testid="spatial-operations-open-latest-historical-scan"
                    >
                      Open latest Scan
                    </Button>
                  ) : null}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onNavigate(scanHistory.historyHref)}
                    data-testid="spatial-operations-view-scan-history"
                  >
                    View Scan history
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Historical Safety Scans are measurement/report history, not Control-linked verification.
                </Typography>
              </>
            ) : (
              <Typography color="text.secondary" data-testid="spatial-operations-no-scan-history">
                No attributable Safety Scan history is available for this condition.
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ border: `1px solid ${colors.line}`, borderRadius: 1, p: 1.5 }} data-testid="spatial-operations-supporting">
          <Typography variant="overline" color="text.secondary">Recorded evidence</Typography>
          {recordedEvidenceUrl ? (
            <Box
              component="img"
              src={recordedEvidenceUrl}
              alt="Recorded condition evidence"
              data-testid="spatial-operations-recorded-evidence"
              sx={{ width: '100%', maxHeight: 180, objectFit: 'contain', my: 1 }}
            />
          ) : (
            <Typography color="text.secondary" data-testid="spatial-operations-no-evidence">
              {enrichmentPending ? SPATIAL_OPERATIONS_ENRICHMENT_LOADING : 'No recorded evidence is linked to this event.'}
            </Typography>
          )}
          {recordedEvidenceHref ? (
            <Button size="small" onClick={() => onNavigate(recordedEvidenceHref)}>Open evidence</Button>
          ) : null}
          {alerts ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }} data-testid="spatial-operations-alerts-copy">
              {alerts.detail}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ border: `1px solid ${colors.line}`, borderRadius: 1, p: 1.5 }} data-testid="spatial-operations-policy">
          <Typography variant="overline" color="text.secondary">Policy context</Typography>
          {policy ? (
            <>
              <Typography variant="body2">{policy.conditionLabel}</Typography>
              <Typography variant="caption" display="block">Scope · {policy.scopeLabel}</Typography>
              {policy.severity ? <Typography variant="caption" display="block">Severity · {policy.severity}</Typography> : null}
              <Typography variant="caption" display="block" data-testid="spatial-operations-policy-immediate">
                Immediate Alert {policy.immediateEnabled == null ? 'unavailable' : (policy.immediateEnabled ? 'enabled' : 'disabled')}
              </Typography>
              <Typography variant="caption" display="block" data-testid="spatial-operations-policy-pattern">
                Pattern Alert {policy.patternEnabled == null ? 'unavailable' : (policy.patternEnabled ? 'enabled' : 'disabled')}
                {policy.patternRule ? ` · ${policy.patternRule}` : ''}
              </Typography>
              {policy.scheduleLabel ? (
                <Typography variant="caption" display="block">Schedule · {policy.scheduleLabel}</Typography>
              ) : null}
            </>
          ) : (
            <Typography color="text.secondary">
              {enrichmentPending ? SPATIAL_OPERATIONS_ENRICHMENT_LOADING : 'No policy context is linked for this condition.'}
            </Typography>
          )}
        </Box>
      </Box>
      </Box>
      )}
    </Box>
  );
}
